import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getLang, getGlobalTranslation } from "store/userSlice";
import { editableTranslate, localTranslate } from "helpers/translation";
import ExpandableHtml from "./ExpandableHtml";
import { Button } from "components/ui";

export default function ErrorAlert({ error, onClose, isHtml = false, type = "danger", ...rest }) {
  const lang = useSelector(getLang);
  const globalTranslation = useSelector(getGlobalTranslation);
  const [showDetails, setShowDetails] = useState(false);

  // Force allowed types only to avoid unexpected styling
  const alertType = type === "warning" ? "warning" : "danger";

  const { text, key, statusCode, reqId, context } = useMemo(() => {
    const message = typeof error === "string" ? error : error?.message ?? "";
    return {
      text: message,
      key: error?.translationKey || "",
      statusCode: error?.statusCode ?? null,
      reqId: error?.reqId ?? null,
      context: error?.context ?? null,
    };
  }, [error]);

  if (!error) return null;

  const isDebugEnv = (() => {
    const env = (process.env.REACT_APP_ENV || "").toLowerCase();
    return env === "local" || env === "dev";
  })();

  const renderTranslated = () => {
    if (!key) return null;
    if (lang && globalTranslation) {
      return editableTranslate(globalTranslation, lang, key, text || key);
    }
    return <>{localTranslate(key, text || key)}</>;
  };

  const renderHtml = () => (
    <ExpandableHtml html={text} className={`fade-${alertType}`} />
  );

  const renderPlain = () => <span>{text || "Unknown error occurred"}</span>;

  const hasDetails = (isDebugEnv && key) || statusCode || reqId || context;

  return (
    <div className={`alert alert-${alertType} ${onClose ? 'alert-dismissible pr-2' : ''} fade show`} role="alert" data-testid="error-alert" {...rest}>
      <div className="d-flex align-items-center">
        <i className="bi bi-exclamation-circle-fill align-self-start mr-2"></i>
        <div className="flex-grow-1">
          {isHtml && typeof error === "string" ? renderHtml() : renderTranslated() || renderPlain()}
        </div>

        {hasDetails && (
          <span
            role="button"
            className="ml-2"
            onClick={() => setShowDetails((s) => !s)}
            style={{ cursor: "pointer", userSelect: "none" }}
            data-testid="error-alert-details-toggle"
          >
            <i
              className={`bi bi-chevron-${showDetails ? "up" : "down"}`}
              style={{ fontSize: "1rem" }}
            />
          </span>
        )}

        {
          onClose && <Button
            variant="ghost"
            size="inline"
            icon="bi-x"
            className="ml-2"
            data-dismiss="alert"
            aria-label="Close"
            onClick={onClose}
            data-testid="error-alert-dismiss"
          />
        }
      </div>

      {showDetails && (
        <div className="mt-2 small text-monospace" data-testid="error-alert-details">
          <ul className="mb-0 pl-3">
            {isDebugEnv && key && (
              <li>
                <strong>translationKey:</strong> <code>{key}</code>
              </li>
            )}
            {statusCode != null && (
              <li>
                <strong>statusCode:</strong> <code>{statusCode}</code>
              </li>
            )}
            {reqId && (
              <li>
                <strong>reqId:</strong> <code>{reqId}</code>
              </li>
            )}
            {context && (
              <li>
                <strong>context:</strong> <code>{String(context)}</code>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
